<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { unauthenticated, UnauthorizedError } from '@/api';
import { loadSession } from '@/session';
import LoginPanel from '@/components/LoginPanel.vue';
import AppShell from '@/components/shell/AppShell.vue';
import { LoaderCircle } from '@lucide/vue';

const error = ref<string | null>(null);

/**
 * Three-way on purpose. Rendering the dashboard chrome while the first /api/admin/me is still in
 * flight meant a signed-out visitor saw the header and a "Loading…" spinner flash past before the
 * login page replaced it — the app looked like it was letting them in and then changing its mind.
 * Nothing that belongs to a session renders until we know there is one.
 */
const authState = ref<'checking' | 'anonymous' | 'ready'>('checking');

// api() flips this on any 401, so a session that expires mid-use drops straight to the login page
// from wherever the user was, without every call site having to handle it.
watch(unauthenticated, isUnauthenticated => {
  if (isUnauthenticated) {
    authState.value = 'anonymous';
  }
});

onMounted(load);

async function load() {
  try {
    await loadSession();

    authState.value = 'ready';
  } catch (e) {
    // A 401 isn't a failure to report — the watcher above has already moved us to the login page.
    if (e instanceof UnauthorizedError) {
      return;
    }

    // Anything else is a real error. Leave 'checking' behind so it's shown inside the normal
    // chrome (AppShell's own error line) rather than trapping the app on a spinner forever.
    authState.value = 'ready';

    error.value = (e as Error).message;
  }
}
</script>

<template>
  <!-- Nothing that belongs to a session renders until we know there is one: no rail to sign out
       of, no empty dashboard, no "Loading…" flash for someone who is about to be shown a login. -->
  <div v-if="authState === 'checking'" class="flex min-h-screen items-center justify-center bg-paper text-ink-3">
    <LoaderCircle class="size-6 animate-spin" />
  </div>

  <LoginPanel v-else-if="authState === 'anonymous'" />

  <AppShell v-else :load-error="error" />
</template>
